<script setup lang="ts">
import { computed } from 'vue';
import BaseSpinner from './BaseSpinner.vue';

interface Props {
  variant?: 'primary' | 'secondary' | 'tertiary';
  disabled?: boolean;
  iconRight?: string;
  iconLeft?: string;
  iconAriaLabel?: string;
  iconLeftAriaLabel?: string;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  class?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  disabled: false,
  loading: false,
  type: 'button',
  iconRight: undefined,
  iconLeft: undefined,
  iconAriaLabel: undefined,
  iconLeftAriaLabel: undefined,
  class: undefined,
});

const emit = defineEmits(['click']);

const buttonClasses = computed(() => {
  const baseClasses = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    tertiary: 'btn-tertiary',
  };

  const classes = [baseClasses[props.variant]];

  if (props.disabled && !props.loading) {
    classes.push('opacity-50');
  }

  classes.push('content-center justify-center');
  if (props.class) {
    classes.push(props.class);
  }

  return classes.join(' ');
});

const iconClasses = computed(() => {
  const iconColors = {
    primary: 'bg-white',
    secondary: 'bg-black',
    tertiary: 'bg-black',
  };

  return `w-6 h-5 icon ${iconColors[props.variant]}`;
});

const handleClick = (event: Event) => {
  if (!props.disabled && !props.loading) {
    emit('click', event);
  } else {
    event.preventDefault();
    event.stopPropagation();
  }
};

const isButtonDisabled = computed(() => props.disabled);
</script>

<template>
  <button
    :class="buttonClasses"
    :disabled="isButtonDisabled"
    :type="type"
    class="relative"
    @click="handleClick"
  >
    <span :class="{ 'opacity-0': loading }" class="inline-flex items-center gap-4">
      <i
        v-if="iconLeft"
        class="icon icon-arrow"
        :class="[iconClasses, iconLeft]"
        :aria-label="iconLeftAriaLabel"
      ></i>
      <slot></slot>
      <i
        v-if="iconRight"
        class="icon icon-arrow"
        :class="[iconClasses, iconRight]"
        :aria-label="iconAriaLabel"
      ></i>
    </span>

    <span v-if="loading" class="absolute inset-0 flex items-center justify-center">
      <BaseSpinner size="md" />
    </span>
  </button>
</template>
